'use client';
import { useEffect, useState, useTransition } from 'react';
import Base from '../../layouts/Base';
import Section from '../../components/Section/Section';
import Map from '../../components/Map/Map';
import HeadLine from '../../components/HeadLine/HeadLine';
import Button from '../../components/Button/Button';
import { t } from '../../i18n';
import s from './WatchtowerPage.module.scss';
import LayerPicker from './layers/layer-picker';
import Countries from './layers/countries';
import ClearAll from './layers/clear-all';
import Flowmap from './filters/flowmap';
import { FlowmapProvider, useFlowmap } from './FlowmapContext';
import { FiltersProvider, useFilters } from './FiltersContext';
import LayerWrapper from './components/LayerWrapper';
import Icon from '../../components/Icon';
import classNames from 'classnames';
import dayjs from 'dayjs';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import Tooltip from '../../components/Tooltip/Tooltip';

const WatchtowerContent = ({
    map,
    showFlowmapLayer,
    mapInstance,
    setMapInstance,
    savedFilters,
    rootUrl,
}) => {
    const searchParams = useSearchParams();
    const { isFiltersShown, setIsFiltersShown } = useFilters();
    const {
        showFlowmap,
        setShowFlowmap,
        activeFilterCount,
        resetFilters,
        setActiveFilterCount,
    } = useFlowmap();
    const [isExpanded, setIsExpanded] = useState(true);
    const [isSavedFiltersExpanded, setIsSavedFiltersExpanded] = useState(true);
    const [mapReady, setMapReady] = useState(false);
    const [filters, setFilters] = useState(savedFilters || []);
    const [isSaved, setIsSaved] = useState(false);
    const [isQueryUpdating, setIsQueryUpdating] = useState(false);
    const [isPending, startTransition] = useTransition();
    const [isLoadingFilter, setIsLoadingFilter] = useState(false);
    const router = useRouter();

    useEffect(() => {
        setIsQueryUpdating(false);
        setIsLoadingFilter(false);
    }, [searchParams]);

    const handleDeleted = (id) => {
        setFilters((prev) => prev.filter((f) => f.id !== id));
    };

    const handleRenamed = (updatedFilter) => {
        setFilters((prev) =>
            prev.map((f) => (f.id === updatedFilter.id ? updatedFilter : f))
        );
    };

    const handleSaved = (newFilter) => {
        setFilters((prev) => [newFilter, ...prev]);
    };

    useEffect(() => {
        if (mapInstance) {
            setMapReady(true);
        }
    }, [mapInstance]);

    const handleFlowmapToggle = (checked) => {
        setShowFlowmap(checked);
    };

    const handleClearAll = () => {
        setShowFlowmap(false);
        resetFilters();
        setIsFiltersShown(false);
        setActiveFilterCount(0);

        sessionStorage.removeItem('showFlowmap');
        sessionStorage.removeItem('activeCountry');
        sessionStorage.removeItem('flowmapFilters');

        const params = new URLSearchParams(searchParams.toString());
        params.delete('flowmap');
        params.delete('activeCountry');
        params.delete('fromCountries');
        params.delete('toCountries');
        params.delete('sources');
        params.delete('types');
        params.delete('hsCodes');
        params.delete('units');
        params.delete('year');

        const nextUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;

        startTransition(() => {
            router.replace(nextUrl, { scroll: false });
        });
    };

    const getCookie = (name) => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
    };

    const handleApplyFilter = (filter) => {
        const newUrl = `${rootUrl}${filter.querystring}`;
        const currentUrl = `${window.location.pathname}${window.location.search}`;

        if (newUrl === currentUrl) {
            setIsLoadingFilter(false);
            return;
        }

        setIsLoadingFilter(true);
        startTransition(() => {
            router.push(newUrl);
        });
    };

    const handleSaveFilters = async () => {
        const csrfToken = getCookie('csrftoken');

        const res = await fetch('/wt/save-filter/', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': csrfToken,
            },
            credentials: 'include',
            body: JSON.stringify({
                querystring: window.location.search,
                title: 'My saved filter',
            }),
        });

        const data = await res.json();
        if (data && data.id) {
            handleSaved(data);
            setIsSaved(true);
            setTimeout(() => setIsSaved(false), 2000);
        }
    };

    const isCurrentFilterSaved = () => {
        const currentQuery = window.location.search;
        return filters.some((filter) => filter.querystring === currentQuery);
    };

    const filtersLayout = ({ minimizeFilter }) => {
        return (
            <div className={s.Filters}>
                <div className={s.FilterDropdown}>
                    <div className={s.FilterHeader}>
                        <HeadLine title="Saved filters" size="Small" />
                        <div className={s.FilterHeaderButtons}>
                            <Button
                                className={s.ClearButton}
                                modifier="Secondary"
                                title={
                                    isSaved
                                        ? t('watchtowerPage.filtersSaved')
                                        : t('watchtowerPage.saveActiveFilters')
                                }
                                icon={
                                    isSaved
                                        ? 'CheckSmallBlackBackground'
                                        : 'SaveFilters'
                                }
                                showIconBefore={true}
                                isSave={true}
                                onClick={handleSaveFilters}
                                disabled={
                                    activeFilterCount === 0 ||
                                    isQueryUpdating ||
                                    isCurrentFilterSaved()
                                }
                                loading={isSaved}
                            />

                            <button
                                className={s.DropdownChevron}
                                onClick={() =>
                                    setIsSavedFiltersExpanded(
                                        !isSavedFiltersExpanded
                                    )
                                }>
                                <Icon
                                    name="SelectChevron"
                                    className={classNames(s.ChevronDown, {
                                        [s.Rotate]: isSavedFiltersExpanded,
                                    })}
                                />
                            </button>
                        </div>
                    </div>
                    <div
                        className={classNames(s.FilterDropdownContent, {
                            [s.Expanded]: isSavedFiltersExpanded,
                        })}>
                        <ul className={s.SavedFiltersList}>
                            {filters && filters.length > 0 ? (
                                filters.map((filter) => (
                                    <SavedFilterItem
                                        key={filter.id}
                                        filter={filter}
                                        onDeleted={handleDeleted}
                                        onRenamed={handleRenamed}
                                        rootUrl={rootUrl}
                                        onApply={handleApplyFilter}
                                        isActive={
                                            filter.querystring ===
                                            window.location.search
                                        }
                                    />
                                ))
                            ) : (
                                <li
                                    className={classNames(
                                        s.SavedFilterItem,
                                        s.NoItems
                                    )}>
                                    <span className={s.NoItemsTitle}>
                                        No saved filters
                                    </span>
                                    <span className={s.NoItemsSubtitle}>
                                        Once you’ve applied filters, select Save
                                        Active Filters to save your view.
                                    </span>
                                </li>
                            )}
                        </ul>
                    </div>
                </div>
                <div className={s.FilterDropdown}>
                    <div className={s.FilterHeader}>
                        <HeadLine title="Flowmap Filters" size="Small" />
                        <span className={s.ActiveFilterCount}>
                            {activeFilterCount > 0
                                ? `(${activeFilterCount})`
                                : ''}
                        </span>
                        <div className={s.FilterHeaderButtons}>
                            {activeFilterCount > 0 && (
                                <Button
                                    className={s.ClearButton}
                                    modifier="Secondary"
                                    title={t('watchtowerPage.clearAll')}
                                    onClick={resetFilters}
                                    disabled={activeFilterCount === 0}
                                />
                            )}

                            <button
                                className={s.DropdownChevron}
                                onClick={() => setIsExpanded(!isExpanded)}>
                                <Icon
                                    name="SelectChevron"
                                    className={classNames(s.ChevronDown, {
                                        [s.Rotate]: isExpanded,
                                    })}
                                />
                            </button>
                        </div>
                    </div>

                    <div
                        className={classNames(s.FilterDropdownContent, {
                            [s.Expanded]: isExpanded,
                        })}>
                        {mapReady && (
                            <Flowmap
                                key="flowmap-sidebar"
                                map={mapInstance}
                                onFilterChange={() => setIsQueryUpdating(true)}
                            />
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className={s.MapWrapper}>
            {/* Headless filtering logic when sidebar is closed */}
            {mapReady && showFlowmap && !isFiltersShown && (
                <Flowmap
                    key="flowmap-headless"
                    map={mapInstance}
                    isFiltersShown={false}
                    onFilterChange={() => setIsQueryUpdating(true)}
                />
            )}

            <Map
                {...map}
                onMapReady={(instance) => {
                    setMapInstance(instance);
                }}
                isFiltersShown={isFiltersShown}
                filtersLayout={filtersLayout}
                isFiltersLoading={isLoadingFilter}
                layers={
                    mapInstance
                        ? [
                              showFlowmapLayer && (
                                  <LayerWrapper key="layer-picker">
                                      <LayerPicker
                                          map={mapInstance}
                                          _handleFlowmapToggle={
                                              handleFlowmapToggle
                                          }
                                      />
                                  </LayerWrapper>
                              ),
                              <LayerWrapper key="countries">
                                  <Countries
                                      countries={map.countryList.countries}
                                  />
                              </LayerWrapper>,
                              showFlowmapLayer && (
                                  <LayerWrapper key="clear-all">
                                      <ClearAll onClick={handleClearAll} />
                                  </LayerWrapper>
                              ),
                              showFlowmap && (
                                  <LayerWrapper key="filter-toggle-button">
                                      <Button
                                          title={
                                              isFiltersShown
                                                  ? t(
                                                        'watchtowerPage.hideFilter'
                                                    )
                                                  : t(
                                                        'watchtowerPage.showFilters'
                                                    )
                                          }
                                          modifier={'FilterAlternative'}
                                          icon={
                                              isFiltersShown
                                                  ? 'PanelCloseBlack'
                                                  : 'PanelOpenWhite'
                                          }
                                          extraClasses={[s.ShowFilterButton]}
                                          isActive={!isFiltersShown}
                                          onClick={() =>
                                              setIsFiltersShown(!isFiltersShown)
                                          }
                                      />
                                  </LayerWrapper>
                              ),
                          ].filter(Boolean)
                        : []
                }
            />
        </div>
    );
};

const WatchtowerPage = ({
    map = {},
    showFlowmapLayer = false,
    savedFilters,
    rootUrl,
}) => {
    const [mapInstance, setMapInstance] = useState(null);

    return (
        <div className={s.Root}>
            <div className={s.Main}>
                <div className={s.Content}>
                    <Section noMarginBottom isMainSection>
                        <FiltersProvider>
                            <FlowmapProvider
                                data={null}
                                map={mapInstance}
                                showFlowmapLayer={showFlowmapLayer}>
                                <WatchtowerContent
                                    map={map}
                                    savedFilters={savedFilters}
                                    showFlowmapLayer={showFlowmapLayer}
                                    mapInstance={mapInstance}
                                    setMapInstance={setMapInstance}
                                    rootUrl={rootUrl}
                                />
                            </FlowmapProvider>
                        </FiltersProvider>
                    </Section>
                </div>
            </div>
        </div>
    );
};

const SavedFilterItem = ({
    filter,
    onDeleted,
    onRenamed,
    rootUrl,
    onApply,
    isActive,
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [title, setTitle] = useState(filter.title);
    const [copied, setCopied] = useState(false);
    const formattedDate = dayjs(filter.createdAt).format('MMM D, HH:mm');

    const csrfToken = document.cookie
        .split('; ')
        .find((row) => row.startsWith('csrftoken='))
        ?.split('=')[1];

    const handleDelete = async () => {
        await fetch(`/wt/delete-filter/${filter.id}/`, {
            method: 'DELETE',
            headers: { 'X-CSRFToken': csrfToken },
            credentials: 'include',
        });
        onDeleted?.(filter.id);
    };

    const handleCopy = () => {
        const fullUrl = `${rootUrl}${filter.querystring}&flowmap=true`;
        navigator.clipboard.writeText(fullUrl);

        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleRename = async () => {
        if (!title.trim()) return;
        const res = await fetch(`/wt/rename-filter/${filter.id}/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': csrfToken,
            },
            body: JSON.stringify({ title }),
            credentials: 'include',
        });
        if (res.ok) {
            const data = await res.json();
            onRenamed?.(data);
        }
        setIsEditing(false);
    };

    const handleClick = () => {
        if (!isActive) {
            onApply?.(filter);
        }
    };

    const classes = classNames(s.SavedFilterItemContent, {
        [s.Active]: isActive,
    });

    return (
        <li className={classes}>
            <div className={s.SavedFilterWrapper} onClick={handleClick}>
                <div className={s.SavedFilterText}>
                    {isEditing ? (
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            onBlur={handleRename}
                            onKeyDown={(e) =>
                                e.key === 'Enter' && handleRename()
                            }
                            autoFocus
                            className={s.RenameInput}
                        />
                    ) : (
                        <div className={s.SavedFilterLink}>
                            <span className={s.SavedFilterTitle}>
                                {filter.title}
                            </span>
                            <span className={s.SavedFilterCreatedDate}>
                                Created {formattedDate}
                            </span>
                        </div>
                    )}
                </div>
                <div className={s.SavedFilterActions}>
                    <Tooltip
                        content={copied ? 'Copied!' : 'Copy link'}
                        showTemporary={copied}
                        direction="top">
                        <button
                            className={s.SavedFilterButton}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleCopy();
                            }}>
                            <Icon name={'Copy'} />
                        </button>
                    </Tooltip>
                    <Tooltip content="Edit name" direction="top">
                        <button
                            className={s.SavedFilterButton}
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsEditing(true);
                            }}>
                            <Icon name="Edit" />
                        </button>
                    </Tooltip>
                    <Tooltip content="Delete filter" direction="top">
                        <button
                            className={s.SavedFilterButton}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDelete();
                            }}>
                            <Icon name="Delete" />
                        </button>
                    </Tooltip>
                </div>
            </div>
        </li>
    );
};

WatchtowerPage.propTypes = {};

export default Base(WatchtowerPage);
